import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Alert,
  LinearProgress,
  Stack,
  Skeleton,
  alpha,
  Dialog,
  DialogContent,
  Fade,
} from '@mui/material';
import {
  LocalFireDepartment as FireIcon,
  EmojiEvents as TrophyIcon,
  RocketLaunch as RocketIcon,
  PublicOutlined as OtrixverseIcon,
} from '@mui/icons-material';
import StreakFlame from 'components/gamification/StreakFlame';
import { GOLD } from 'common/theme/button/premiumButtonSx';

// Curva de salida premium (soft-skill) para el hover de las cards monocromas.
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// GraphQL Query
const MY_GLOBAL_RANK_DATA = gql`
  query MyGlobalRankData {
    myGlobalRankData {
      user_id
      gender
      total_points
      global_rank
      mythological_rank
      mythological_rank_info {
        name
        name_en
        description
        color
        icon
      }
      previous_rank
      previous_mythological_rank
      total_exercise_time_seconds
      total_exercises_completed
      total_sessions_completed
      monthly_sessions_completed
      intensity_score
      current_streak_days
      progress_to_next_rank
      total_active_users
      last_calculated_at
      monthly_base_score
      monthly_total_score
      annual_base_score
    }
  }
`;

/**
 * MyGamificationProfilePage - Perfil completo de gamificación del usuario
 * Accesible para todos los roles: ADMIN, TRAINER, CLIENT, SUPERVISOR
 *
 * Fuente única OTRIXVERSE: el puntaje del año (annual_base_score) define el ranking.
 * Sin bonus Top 3 ni fórmula multifactor.
 */
function MyGamificationProfilePage() {
  const { t } = useTranslation(['common']);
  const { loading, data, error } = useQuery(MY_GLOBAL_RANK_DATA, { fetchPolicy: 'cache-and-network' });
  const [isRankModalOpen, setIsRankModalOpen] = useState(false);

  // Log error para debugging
  if (error) {
    console.error('MyGlobalRankData Error:', error);
    console.error('GraphQL Errors:', error.graphQLErrors);
    console.error('Network Error:', error.networkError);
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  // Mostrar error de debugging si existe
  const showDebugError = error && process.env.NODE_ENV === 'development';

  // Si hay error o no hay datos, usar valores por defecto
  const rankData = data?.myGlobalRankData || {
    user_id: null,
    gender: null,
    total_points: 0,
    global_rank: null,
    mythological_rank: 'HUMAN',
    mythological_rank_info: {
      name: 'Humano',
      name_en: 'Human',
      description: 'Nuevo en el camino del Olimpo',
      description_en: 'New to the path of Olympus',
      color: '#94a3b8',
      icon: '⚡',
    },
    previous_rank: null,
    previous_mythological_rank: null,
    total_exercise_time_seconds: 0,
    total_exercises_completed: 0,
    total_sessions_completed: 0,
    intensity_score: 0,
    current_streak_days: 0,
    progress_to_next_rank: 0,
    total_active_users: 1,
    last_calculated_at: null,
    monthly_base_score: 0,
    monthly_total_score: 0,
    annual_base_score: 0,
    monthly_sessions_completed: 0,
  };

  const isNewUser = !data?.myGlobalRankData;
  const rankInfo = rankData.mythological_rank_info;

  // Obtener URL de la imagen del rango según género del usuario actual
  const getRankImage = () => {
    if (!rankData.mythological_rank) return null;
    const rankName = rankData.mythological_rank.toLowerCase();

    // Usar sufijo _M para mujeres (FEMALE), sin sufijo para hombres (MALE)
    const genderSuffix = rankData.gender === 'FEMALE' ? '_M' : '';
    return `/images/ranks/${rankName}${genderSuffix}.png`;
  };

  // Datos del score OTRIXVERSE (fuente única)
  const monthlyBaseScore = rankData.monthly_base_score || 0;   // Score de este mes (para la barra)
  const annualBaseScore  = rankData.annual_base_score  || 0;   // Acumulado del año (número grande)
  const sessionsThisYear = rankData.monthly_sessions_completed ?? 0;

  const breakdownCategories = [
    {
      key: 'otrixverse',
      title: 'Score OTRIXVERSE',
      icon: OtrixverseIcon,
      mainValue: annualBaseScore.toFixed(1),              // Número grande: acumulado del año
      progressValue: Math.min((monthlyBaseScore / 120) * 100, 100), // Barra: solo este mes
    },
    {
      key: 'streak',
      title: t('myRank.breakdown.streak', { ns: 'common', defaultValue: 'Racha de sesiones' }),
      icon: FireIcon,
      mainValue: sessionsThisYear,
      progressValue: Math.min((sessionsThisYear / 25) * 100, 100),
      streakDays: sessionsThisYear,
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Error de debugging (solo en desarrollo) */}
      {showDebugError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>GraphQL Error (Debug)</Typography>
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
            {JSON.stringify(error, null, 2)}
          </Typography>
        </Alert>
      )}

      {/* Hero Section con Rango */}
      <Paper
        elevation={3}
        sx={{
          mb: 4,
          borderRadius: 2,
          backgroundColor: 'surface.content',
          border: '1px solid',
          borderColor: 'hairline.main',
          boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
          color: 'text.primary',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Grid container spacing={3} alignItems="center">
            {/* Izquierda: Badge y Rango */}
            <Grid item xs={12} md={8}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                {/* Badge del rango */}
                <Box
                  onClick={() => {
                    if (getRankImage()) setIsRankModalOpen(true);
                  }}
                  sx={{
                    width: { xs: 100, md: 120 },
                    height: { xs: 100, md: 120 },
                    borderRadius: '50%',
                    background: 'var(--otx-surface-hover)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 8px 32px ${alpha('#181d36', 0.4)}`,
                    border: '3px solid var(--otx-surface-hover)',
                    cursor: getRankImage() ? 'pointer' : 'default',
                    transition: 'all 0.3s ease',
                    '&:hover': getRankImage() ? {
                      transform: 'scale(1.08)',
                      boxShadow: `0 0 40px ${alpha('#F9BD4E', 0.7)}, 0 8px 32px ${alpha('#F9BD4E', 0.5)}`,
                      background: 'var(--otx-surface-hover)',
                    } : {},
                    overflow: 'hidden',
                    padding: 0.5,
                  }}
                >
                  {getRankImage() ? (
                    <Box
                      component="img"
                      src={getRankImage()}
                      alt={rankInfo.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%',
                      }}
                    />
                  ) : (
                    <Box sx={{ fontSize: { xs: '3rem', md: '4rem' } }}>
                      {rankInfo.icon}
                    </Box>
                  )}
                </Box>

                {/* Información del rango */}
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {rankInfo.name}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                    {rankInfo.description}
                  </Typography>
                  {rankData.global_rank ? (
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>
                      {t('myRank.position', {
                        ns: 'common',
                        rank: rankData.global_rank,
                        total: rankData.total_active_users.toLocaleString(),
                      })}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>
                      {t('myRank.startJourney', { ns: 'common' })}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Grid>

            {/* Derecha: Puntos */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                <Typography variant="body2" sx={{ opacity: 0.75, mb: 1 }}>
                  {t('myRank.totalPoints', { ns: 'common' })}
                </Typography>
                <Typography variant="h2" sx={{ fontWeight: 700 }}>
                  {Math.round(rankData.total_points).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Progreso hacia siguiente rango */}
          {rankData.mythological_rank !== 'TITAN' && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                {t('myRank.progressToNextRank', { ns: 'common' })}
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <LinearProgress
                  variant="determinate"
                  value={rankData.progress_to_next_rank}
                  sx={{
                    height: 12,
                    borderRadius: 2,
                    bgcolor: 'var(--otx-surface-hover)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#F9BD4E',
                      borderRadius: 2,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontWeight: 700,
                    color: 'text.primary',
                  }}
                >
                  {Math.round(rankData.progress_to_next_rank)}%
                </Typography>
              </Box>
            </Box>
          )}

          {/* Mensaje especial para Titan */}
          {rankData.mythological_rank === 'TITAN' && !isNewUser && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: 'var(--otx-surface-hover)',
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <TrophyIcon sx={{ color: 'text.primary', fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{
                  color: 'text.primary',
                  fontWeight: 700,
                  textAlign: 'center',
                }}
              >
                {t('myRank.titanMessage', { ns: 'common' })}
              </Typography>
              <TrophyIcon sx={{ color: 'text.primary', fontSize: 28 }} />
            </Box>
          )}
        </Box>
      </Paper>

      {/* Banner Motivacional para Nuevos Usuarios */}
      {isNewUser && (
        <Alert
          severity="info"
          icon={<RocketIcon sx={{ fontSize: 32, color: 'var(--otx-gold-text)' }} />}
          sx={{
            mb: 4,
            py: 2,
            px: 3,
            color: 'text.primary',
            bgcolor: 'surface.overlay',
            border: '1px solid',
            borderColor: 'hairline.main',
            borderRadius: 2,
            '& .MuiAlert-icon': {
              alignItems: 'center',
            },
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
              {t('myRank.welcomeTitle', { ns: 'common' })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {t('myRank.welcomeMessage', { ns: 'common' })}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--otx-gold-text)' }}>
              {t('myRank.welcomeAction', { ns: 'common' })}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Score OTRIXVERSE y actividad */}
      <Grid container spacing={3} sx={{ mb: 4, alignItems: 'stretch', justifyContent: 'center' }}>
          {breakdownCategories.map((cat) => {
            const IconComponent = cat.icon;
            return (
              <Grid item xs={12} sm={6} lg={4} key={cat.key} sx={{ display: 'flex' }}>
                <Paper
                  elevation={2}
                  sx={{
                    overflow: 'hidden',
                    backgroundColor: 'surface.content',
                    border: '1px solid',
                    borderColor: 'hairline.main',
                    boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
                    transition: `transform 0.25s ${EASE}, box-shadow 0.25s ${EASE}, border-color 0.25s ${EASE}, background-color 0.25s ${EASE}`,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      backgroundColor: 'surface.hover',
                      borderColor: 'hairline.strong',
                      boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow-hover)',
                    },
                  }}
                >
                  {/* Header monocromo: ícono en dorado (acento), título en blanco */}
                  <Box
                    sx={{
                      backgroundColor: 'surface.raised',
                      borderBottom: '1px solid',
                      borderColor: 'hairline.main',
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      minHeight: { lg: 72 },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, overflow: 'hidden' }}>
                      <IconComponent sx={{ color: 'var(--otx-gold-text)', fontSize: { xs: 28, lg: 20, xl: 24 }, flexShrink: 0 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 600,
                          fontSize: { xs: '0.875rem', lg: '0.75rem', xl: '0.8125rem' },
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {cat.title}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Body */}
                  <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        mb: 0.5,
                      }}
                    >
                      {cat.mainValue}
                    </Typography>

                    {/* OTRIXVERSE: acumulado del año + barra del mes */}
                    {cat.key === 'otrixverse' && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block', mb: 1.5, flex: 1, minHeight: 52, lineHeight: 1.5 }}
                      >
                        Puntaje acumulado del año · este mes suma hasta 120 pts
                      </Typography>
                    )}

                    {/* Sesiones: actividad (no afecta el ranking) + llama */}
                    {cat.key === 'streak' && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block', mb: 1.5, flex: 1, minHeight: 52, lineHeight: 1.5 }}
                        >
                          Sesiones realizadas este año · actividad, no afecta el ranking
                        </Typography>
                        {cat.streakDays > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <StreakFlame streakDays={cat.streakDays} size="md" showLabel />
                          </Box>
                        )}
                      </>
                    )}

                    {/* Barra de progreso (acento dorado) */}
                    <LinearProgress
                      variant="determinate"
                      value={cat.progressValue}
                      sx={{
                        height: 6,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: cat.progressValue > 0 ? GOLD : 'transparent',
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

      {/* Última actualización */}
      {rankData.last_calculated_at && !isNewUser && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('myRank.lastUpdated', { ns: 'common' })}:{' '}
            {new Date(rankData.last_calculated_at).toLocaleString('es-ES', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      )}

      {/* Modal para ver imagen del rango en grande */}
      <Dialog
        open={isRankModalOpen}
        onClose={() => setIsRankModalOpen(false)}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
        BackdropProps={{
          sx: {
            bgcolor: alpha('#000', 0.85),
            backdropFilter: 'blur(8px)',
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* Imagen grande del rango */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: 250, sm: 300 },
              height: { xs: 250, sm: 300 },
              borderRadius: '50%',
              bgcolor: alpha(rankInfo.color, 0.15),
              border: `3px solid ${rankInfo.color}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 40px ${alpha(rankInfo.color, 0.6)}, 0 0 80px ${alpha(rankInfo.color, 0.3)}`,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  boxShadow: `0 0 40px ${alpha(rankInfo.color, 0.6)}, 0 0 80px ${alpha(rankInfo.color, 0.3)}`,
                },
                '50%': {
                  boxShadow: `0 0 60px ${alpha(rankInfo.color, 0.8)}, 0 0 120px ${alpha(rankInfo.color, 0.5)}`,
                },
              },
            }}
          >
            <Box
              component="img"
              src={getRankImage()}
              alt={rankInfo.name}
              sx={{
                width: '90%',
                height: '90%',
                objectFit: 'cover',
                borderRadius: '50%',
              }}
            />
          </Box>

          {/* Información del rango */}
          <Box
            sx={{
              textAlign: 'center',
              bgcolor: alpha('#1F2327', 0.95),
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              p: 3,
              border: `1px solid ${alpha(rankInfo.color, 0.3)}`,
              minWidth: { xs: 280, sm: 350 },
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: rankInfo.color,
                mb: 1,
                textShadow: `0 0 20px ${alpha(rankInfo.color, 0.5)}`,
              }}
            >
              {rankInfo.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              {rankInfo.description}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
}

export default MyGamificationProfilePage;
